import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Keycloak from 'keycloak-js';

import { ReactKeycloakProvider } from '@react-keycloak/web';

import { sessionsDestroy } from '@/services/Sessions';
import { useUserContext } from '../user';

import { KeycloakContext } from './context';

const KeycloakProvider = ({ children }) => {
  const {
    userSession,
    logoutUser,
    handleLoginWithToken,
  } = useUserContext();

  const {
    realmId,
    authHost,
    external,
    authenticated,
  } = userSession;

  const canKeycloak = realmId && authHost && external;

  const authClient = useMemo(() => (
    canKeycloak ? new Keycloak({
      url: authHost,
      realm: realmId,
      clientId: 'datanomik',
    }) : null
  ), [realmId, authHost, canKeycloak]);

  const handleToken = async ({ idToken, refreshToken, token }) => {
    if (idToken && external && !authenticated) {
      handleLoginWithToken({ idToken, refreshToken, token });
    }
  };

  const handleLoginKeycloak = async () => {
    if (canKeycloak) {
      await authClient.login();
    }
  };

  const handleLogoutKeycloak = async () => {
    if (canKeycloak) {
      await authClient.logout(
        { redirectUri: window.location.href.replace('#survey-1', '') },
      );
    }

    await sessionsDestroy();
    logoutUser();
  };

  const handleCleanKeycloak = async () => {
    if (canKeycloak) {
      await authClient.logout(
        { redirectUri: window.location.href.replace('#survey-1', '') },
      );
    }

    logoutUser();
  };

  const keycloakValue = useMemo(() => ({
    keycloack: authClient,
    loginKeycloak: handleLoginKeycloak,
    logoutKeycloak: handleLogoutKeycloak,
    cleanKeycloak: handleCleanKeycloak,
  }), [authClient]);

  return (
    <KeycloakContext.Provider value={keycloakValue}>
      {canKeycloak ? (
        <ReactKeycloakProvider
          authClient={authClient}
          onTokens={handleToken}
          initOptions={{
            onLoad: 'login-required',
            checkLoginIframe: false,
            pkceMethod: 'S256',
            responseMode: 'query',
          }}
        >
          {children}
        </ReactKeycloakProvider>
      ) : children}

    </KeycloakContext.Provider>
  );
};

KeycloakProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default KeycloakProvider;
